import { GameScore, ScoreContextType } from '@/types';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useUser } from './UserContext';

// Import Firebase functions and auth instance
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '@/config/firebase';
import { doc, onSnapshot } from 'firebase/firestore'; // Import firestore functions


// Get a reference to the reportActivity Cloud Function
const reportActivityCallable = httpsCallable(functions, 'reportActivity');

// Define the expected response type from reportActivity
// type ReportActivityResponse = { data: { score: number; success: boolean; } };


const ScoreContext = createContext<ScoreContextType | undefined>(undefined);

export const ScoreProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser(); // Assuming useUser provides the authenticated Firebase user

  const [score, setScore] = useState(0);

  const [topScores, setTopScores] = useState<GameScore[]>([]); // Still fetch top scores for leaderboard
  const [isActive, setIsActive] = useState(false); // Still track client activity state
  const [isGameActive, setIsGameActive] = useState(false);

  const INACTIVITY_TIMEOUT = 10 * 1000; // 10 seconds
  const activityEvents = ['mousemove', 'keydown', 'scroll', 'click'];
  const expiryTime = useRef(Date.now() + INACTIVITY_TIMEOUT);


  const updateExpiryTime = () => {
    // Only track activity if a user is logged in and the game is active
    if (user && isGameActive) {
      expiryTime.current = Date.now() + INACTIVITY_TIMEOUT;
      // console.info('[ScoreContext] User activity detected');
    }
  };

  const checkForInactivity = () => {
    // Check if the game is active and a user is logged in
    if (user && isGameActive) {
        const currentTime = Date.now();
        const userIsActive = currentTime < expiryTime.current;

        // Update isActive state based on inactivity timeout
        if (isActive !== userIsActive) {
            setIsActive(userIsActive);
            // console.info(`[ScoreContext] User activity status changed: ${userIsActive ? 'Active' : 'Inactive'}`);
        }
    }
  };

  // Update expiry time on any user activity
  useEffect(() => {
    if (user && isGameActive) {
      updateExpiryTime(); // Initial update on effect run

      activityEvents.forEach(event => {
        window.addEventListener(event, updateExpiryTime);
      });

      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, updateExpiryTime);
        });
      };
    }
    // Cleanup listeners when user or isGameActive changes (e.g., user logs out, game stops)
    return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, updateExpiryTime);
        });
    };
  }, [user, isGameActive]); // Depend on user and isGameActive

  // Check for inactivity periodically (e.g., every second)
  useEffect(() => {
    let inactivityInterval: NodeJS.Timeout | null = null;
    if (user && isGameActive) {
      inactivityInterval = setInterval(checkForInactivity, 1000);
    }

    return () => {
      if (inactivityInterval) {
        clearInterval(inactivityInterval);
      }
    };
  }, [user, isGameActive, isActive]);


  useEffect(() => {
    let reportInterval: NodeJS.Timeout | null = null;

    // Only report if user is logged in and game is active
    if (user && isGameActive) {
        const reportActivity = async () => {
            try {
                //  console.log(`[ScoreContext] Reporting activity to server for user: ${user.uid}`);
                 await reportActivityCallable({ /* optional data */ });
                //  console.log('[ScoreContext] Activity reported successfully.');

            } catch (error) {
                 console.error('[ScoreContext] Error reporting activity to server:', error);
            }
        };

        // Report activity periodically while the game is active
        reportInterval = setInterval(reportActivity, 20000); // Match the server's scoring interval

        // Also report activity immediately when game becomes active
        reportActivity();

    }

    return () => {
      if (reportInterval) {
        clearInterval(reportInterval);
      }
    };
    // Depend on user and isGameActive. isActive is handled by the checkForInactivity effect.
  }, [user, isGameActive]);


  // Listen to the user's official score document in Firestore
  useEffect(() => {
      let unsubscribeScore: () => void;

      if (user) {
          // console.log(`[ScoreContext] Setting up Firestore listener for user score: ${user.uid}`);
          const userScoreDocRef = doc(db, 'scores', user.uid); // Listen to the user's specific score document

          unsubscribeScore = onSnapshot(userScoreDocRef, (docSnapshot) => {
              if (docSnapshot.exists()) {
                  const userData = docSnapshot.data();
                  const latestScore = userData.score || 0;
                  // console.log(`[ScoreContext] Received Firestore score update for ${user.uid}: ${latestScore}`);
                  setScore(latestScore); // Update the client-side score state with the official score
              } else {
                  // console.log(`[ScoreContext] User score document not found for ${user.uid}. Score set to 0.`);
                  setScore(0); // Set score to 0 if document doesn't exist
              }
          }, (error) => {
              console.error(`[ScoreContext] Error listening to user score document ${user.uid}:`, error);

          });
      }

      // Cleanup listener when user logs out or component unmounts
      return () => {
          if (unsubscribeScore) {
              // console.log(`[ScoreContext] Cleaning up Firestore listener for user score: ${user?.uid}`);
              unsubscribeScore();
          }
      };
  }, [user]);


  const startScoring = () => {
    setIsGameActive(true); // Indicate game is active
    // console.log('[ScoreContext] Scoring started (game active).');
  };

  const stopScoring = async () => {
    setIsGameActive(false); // Indicate game is inactive
    setIsActive(false); // Ensure activity state is false


    // console.log('[ScoreContext] Scoring stopped (game inactive).');
  };

  const toggleGameState = (active: boolean) => {
    setIsGameActive(active);
     if (!active) {
         setIsActive(false); // Ensure inactive when game is not active
     }
  };


  return (
    <ScoreContext.Provider
      value={{
        score,
        isActive,
        isGameActive,
        startScoring,
        stopScoring,
        toggleGameState,
        topScores,
      }}
    >
      {children}
    </ScoreContext.Provider>
  );
};

export const useScore = () => {
  const context = useContext(ScoreContext);
  if (context === undefined) {
    throw new Error('useScore must be used within a ScoreProvider');
  }
  return context;
};